import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { continentsAPI } from '../lib/api';
import { logger } from '../lib/logger';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { ChevronLeft, Utensils, ChevronRight } from 'lucide-react';

const CONTINENT_DATA = {
  africa: {
    name: 'Afrique',
    color: 'from-continent-africa',
    bgColor: 'bg-continent-africa',
    image: 'https://images.unsplash.com/photo-1700625914294-d3246e005957?w=1600&q=80',
    description: 'Découvrez les saveurs épicées et les traditions culinaires millénaires du continent africain.',
  },
  asia: {
    name: 'Asie',
    color: 'from-continent-asia',
    bgColor: 'bg-continent-asia',
    image: 'https://images.unsplash.com/photo-1711633648854-50a30a6df74d?w=1600&q=80',
    description: 'De la street food aux plats impériaux, explorez la diversité culinaire asiatique.',
  },
  europe: {
    name: 'Europe',
    color: 'from-continent-europe',
    bgColor: 'bg-continent-europe',
    image: 'https://images.unsplash.com/photo-1642346854073-a88b5a77038c?w=1600&q=80',
    description: 'Gastronomie raffinée et terroirs authentiques des pays européens.',
  },
  north_america: {
    name: 'Amérique du Nord',
    color: 'from-continent-north-america',
    bgColor: 'bg-continent-north-america',
    image: 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=1600&q=80',
    description: 'Du comfort food américain aux saveurs caribéennes.',
  },
  south_america: {
    name: 'Amérique du Sud',
    color: 'from-continent-south-america',
    bgColor: 'bg-continent-south-america',
    image: 'https://images.unsplash.com/photo-1650539062879-1ec3b24f1bde?w=1600&q=80',
    description: 'Couleurs vibrantes et ingrédients uniques de l\'Amérique latine.',
  },
  oceania: {
    name: 'Océanie',
    color: 'from-continent-oceania',
    bgColor: 'bg-continent-oceania',
    image: 'https://images.unsplash.com/photo-1571167530149-c1105da4c2c7?w=1600&q=80',
    description: 'Fusion des îles du Pacifique et saveurs australiennes.',
  },
};

const COUNTRY_FLAGS = {
  // Africa
  'Maroc': '🇲🇦', 'Tunisie': '🇹🇳', 'Algérie': '🇩🇿', 'Sénégal': '🇸🇳', 'Éthiopie': '🇪🇹',
  'Afrique du Sud': '🇿🇦', 'Égypte': '🇪🇬', 'Nigeria': '🇳🇬', 'Kenya': '🇰🇪', "Côte d'Ivoire": '🇨🇮',
  // Asia
  'Japon': '🇯🇵', 'Chine': '🇨🇳', 'Thaïlande': '🇹🇭', 'Vietnam': '🇻🇳', 'Inde': '🇮🇳',
  'Corée du Sud': '🇰🇷', 'Indonésie': '🇮🇩', 'Philippines': '🇵🇭', 'Malaisie': '🇲🇾', 'Turquie': '🇹🇷',
  // Europe
  'France': '🇫🇷', 'Italie': '🇮🇹', 'Espagne': '🇪🇸', 'Grèce': '🇬🇷', 'Portugal': '🇵🇹',
  'Allemagne': '🇩🇪', 'Royaume-Uni': '🇬🇧', 'Belgique': '🇧🇪', 'Suisse': '🇨🇭', 'Pays-Bas': '🇳🇱',
  // North America
  'États-Unis': '🇺🇸', 'Mexique': '🇲🇽', 'Canada': '🇨🇦', 'Cuba': '🇨🇺', 'Jamaïque': '🇯🇲',
  'Haïti': '🇭🇹', 'République Dominicaine': '🇩🇴', 'Guatemala': '🇬🇹', 'Honduras': '🇭🇳', 'Costa Rica': '🇨🇷',
  // South America
  'Brésil': '🇧🇷', 'Argentine': '🇦🇷', 'Pérou': '🇵🇪', 'Colombie': '🇨🇴', 'Chili': '🇨🇱',
  'Venezuela': '🇻🇪', 'Équateur': '🇪🇨', 'Bolivie': '🇧🇴', 'Paraguay': '🇵🇾', 'Uruguay': '🇺🇾',
  // Oceania
  'Australie': '🇦🇺', 'Nouvelle-Zélande': '🇳🇿', 'Fidji': '🇫🇯', 'Papouasie-Nouvelle-Guinée': '🇵🇬',
  'Samoa': '🇼🇸', 'Tonga': '🇹🇴', 'Vanuatu': '🇻🇺', 'Nouvelle-Calédonie': '🇳🇨', 'Tahiti': '🇵🇫', 'Hawaï': '🇺🇸',
};

export default function ContinentPage() {
  const { continentId } = useParams();
  const [continent, setContinent] = useState(null);
  const [loading, setLoading] = useState(true);

  const continentInfo = CONTINENT_DATA[continentId] || {
    name: 'Continent',
    color: 'from-worldplate-primary',
    bgColor: 'bg-worldplate-primary',
    image: 'https://images.unsplash.com/photo-1699572583203-97170ae281c0?w=1600&q=80',
    description: 'Découvrez les saveurs de ce continent.',
  };

  useEffect(() => {
    const fetchContinent = async () => {
      try {
        const response = await continentsAPI.getById(continentId);
        setContinent(response.data);
      } catch (error) {
        logger.error('Failed to fetch continent:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContinent();
  }, [continentId]);

  return (
    <div className="min-h-screen bg-worldplate-cream">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]" data-testid="continent-hero">
        <div className="absolute inset-0">
          <img
            src={continentInfo.image}
            alt={continentInfo.name}
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${continentInfo.color} to-transparent opacity-80`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-12">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4 -ml-2" data-testid="back-to-home-btn">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
              {continentInfo.name}
            </h1>
            <p className="text-lg text-white/90 max-w-2xl">
              {continentInfo.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Countries Grid */}
      <section className="py-16 md:py-24" data-testid="countries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-10">
            <span className="text-worldplate-primary font-accent text-xl">Explorez par</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-worldplate-deep mt-2">
              Pays
            </h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-32 rounded-2xl bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {continent?.countries.map((country, index) => (
                <motion.div
                  key={country.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <Link
                    to={`/countries/${encodeURIComponent(country.name)}`}
                    className="block group"
                    data-testid={`country-card-${country.name}`}
                  >
                    <div className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-worldplate-primary/30 hover:shadow-lg transition-shadow group-hover:-translate-y-1 transition-transform">
                      <div className="text-4xl mb-3">
                        {COUNTRY_FLAGS[country.name] || '🌍'}
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-worldplate-deep group-hover:text-worldplate-primary transition-colors">
                        {country.name}
                      </h3>
                      <div className="flex items-center gap-1 text-sm text-worldplate-muted mt-2">
                        <Utensils className="w-4 h-4" />
                        <span>{country.recipe_count} recettes</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-300 group-hover:text-worldplate-primary absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
